import React, { useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const COLORS = ['#00ff88','#00d4ff','#9d4edd','#ff6b9d','#ffd700','#ff4444','#00ff88','#00d4ff','#9d4edd','#ff6b9d'];

const CHANNELS = [
  { id: 'all',    label: 'All Channels' },
  { id: 'online', label: '🌐 Online' },
  { id: 'pos',    label: '🏪 POS' },
];

function getRevenue(item, ch) {
  if (ch === 'online') return item.online_revenue || 0;
  if (ch === 'pos') return item.pos_revenue || 0;
  return item.revenue;
}

function getUnits(item, ch) {
  if (ch === 'online') return item.online_units || 0;
  if (ch === 'pos') return item.pos_units || 0;
  return item.units_sold;
}

export default function TopProducts({ data, compact }) {
  const [channel, setChannel] = useState('all');

  if (!data || !data.length) return <div className="inv-empty">No product data</div>;

  const sorted = [...data]
    .map(item => ({ ...item, _rev: getRevenue(item, channel), _units: getUnits(item, channel) }))
    .sort((a, b) => b._rev - a._rev);

  const display = compact ? sorted.slice(0, 5) : sorted;
  const totalRev = display.reduce((s, d) => s + d._rev, 0);

  const barData = {
    labels: display.map(d => d.title.length > 25 ? d.title.slice(0, 25) + '…' : d.title),
    datasets: [{
      label: channel === 'online' ? '🌐 Online Revenue (AED)' : channel === 'pos' ? '🏪 POS Revenue (AED)' : 'Revenue (AED)',
      data: display.map(d => d._rev),
      backgroundColor: display.map((_, i) => COLORS[i % COLORS.length] + '40'),
      borderColor: display.map((_, i) => COLORS[i % COLORS.length]),
      borderWidth: 2, borderRadius: 8, barPercentage: 0.7,
    }]
  };

  const options = {
    indexAxis: 'y', responsive: true, maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(22,22,42,0.95)', titleColor: '#fff', bodyColor: '#a0a0b0',
        borderColor: 'rgba(0,255,136,0.3)', borderWidth: 1,
        callbacks: {
          label: (ctx) => {
            const item = display[ctx.dataIndex];
            const lines = [`Revenue: AED ${item._rev.toLocaleString()}`, `Units: ${item._units}`];
            if (channel === 'all') {
              lines.push(`Online: AED ${(item.online_revenue||0).toLocaleString()} (${item.online_units||0} u)`);
              lines.push(`POS: AED ${(item.pos_revenue||0).toLocaleString()} (${item.pos_units||0} u)`);
            }
            return lines;
          }
        }
      }
    },
    scales: {
      x: {
        grid: { color: 'rgba(255,255,255,0.03)' },
        ticks: { color: '#00ff88', font: { size: 10 }, callback: v => `${(v/1000).toFixed(0)}k` }
      },
      y: { grid: { display: false }, ticks: { color: '#a0a0b0', font: { size: 11 } } }
    }
  };

  return (
    <div className="inv-chart-container">
      <div className="inv-chart-header">
        <h2 className="inv-chart-title">
          <span>🏆</span> Top {display.length} Products
          <span className="inv-badge">by revenue</span>
        </h2>
        <div className="inv-chart-view-tabs">
          {CHANNELS.map(c => (
            <button
              key={c.id}
              className={`inv-chart-tab ${channel === c.id ? 'active' : ''}`}
              onClick={() => setChannel(c.id)}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ height: compact ? '300px' : `${display.length * 55 + 60}px`, position: 'relative' }}>
        <Bar data={barData} options={options} />
      </div>

      {!compact && (
        <div className="inv-product-list">
          {display.map((item, i) => (
            <div key={i} className="inv-product-row">
              <span className="inv-rank">#{i + 1}</span>
              <span className="inv-name">{item.title}</span>
              <span className="inv-units">{item._units} units</span>
              <span className="inv-revenue">AED {item._rev.toLocaleString()}</span>
              {channel === 'all' && (
                <span className="inv-ch-split-mini">
                  <span className="online-pill">🌐 {item.online_units || 0}</span>
                  <span className="pos-pill">🏪 {item.pos_units || 0}</span>
                </span>
              )}
              <span className="inv-pct">{totalRev > 0 ? (item._rev / totalRev * 100).toFixed(1) : 0}%</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
